import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Stack,
  alpha,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  LinkOff as UnlinkIcon,
  Link as LinkIcon,
  DeleteOutline as DeactivateIcon,
  SwapHoriz as RelinkIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import repairService, {
  OrphanedItem,
  OrphanedItemCandidate,
  UnlinkedWli,
  InventoryItemCandidate,
} from '../../../services/repairService';
import venueService from '../../../services/venueService';
import type { Venue } from '../../../types/venue';

// ── Relink dialog (orphaned items) ────────────────────────────────────────────

interface RelinkDialogProps {
  item:     OrphanedItem;
  onClose:  () => void;
  onDone:   (msg: string) => void;
}

const RelinkDialog: React.FC<RelinkDialogProps> = ({ item, onClose, onDone }) => {
  const [targetWineId, setTargetWineId] = useState<string>(
    item.candidates.length === 1 ? item.candidates[0].wineId : ''
  );
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const handleRelink = async () => {
    if (!targetWineId) return;
    setError(null);
    setLoading(true);
    try {
      await repairService.relinkOrphanedItem(item.id, targetWineId);
      onDone('Item successfully re-linked.');
    } catch (e: any) {
      setError(e?.response?.data?.error?.message ?? 'Re-link failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const displayLabel = item.wineDisplay?.name || item.inactiveWine.name || '(unknown)';

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={onClose}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Re-link Inventory Item</Typography>
        <Typography variant="body2" color="text.secondary">
          {displayLabel}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          This item currently points to an <strong>inactive wine</strong>. Choose an active wine
          to re-point it to. Stock balance and movement history are preserved.
        </Alert>

        {item.candidates.length === 0 ? (
          <Alert severity="warning">
            No candidate wines found. All active wines with the same wine name already have
            an inventory item at this location.
            {item.candidatesExcluded > 0 && ` (${item.candidatesExcluded} excluded due to scope collision)`}
          </Alert>
        ) : (
          <>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Select target wine
              {item.candidatesExcluded > 0 && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ({item.candidatesExcluded} hidden — scope collision)
                </Typography>
              )}
            </Typography>
            <RadioGroup value={targetWineId} onChange={e => setTargetWineId(e.target.value)}>
              {item.candidates.map((c: OrphanedItemCandidate) => (
                <FormControlLabel
                  key={c.wineId}
                  value={c.wineId}
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{c.name || '(unnamed)'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.vintageType === 'single_vintage' ? c.displayVintage
                          : c.vintageType === 'non_vintage' ? 'NV' : 'Multi-vintage'}
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 0.5, alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
                />
              ))}
            </RadioGroup>
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          onClick={handleRelink}
          disabled={!targetWineId || item.candidates.length === 0 || loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RelinkIcon />}
        >
          {loading ? 'Re-linking…' : 'Re-link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Deactivate confirmation dialog (orphaned items) ───────────────────────────

interface DeactivateDialogProps {
  item:    OrphanedItem;
  onClose: () => void;
  onDone:  (msg: string) => void;
}

const DeactivateDialog: React.FC<DeactivateDialogProps> = ({ item, onClose, onDone }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const handleDeactivate = async () => {
    setError(null);
    setLoading(true);
    try {
      await repairService.deactivateOrphanedItem(item.id);
      onDone('Item deactivated.');
    } catch (e: any) {
      setError(e?.response?.data?.error?.message ?? 'Deactivation failed.');
    } finally {
      setLoading(false);
    }
  };

  const displayLabel = item.wineDisplay?.name || item.inactiveWine.name || '(unknown)';

  return (
    <Dialog open maxWidth="xs" fullWidth onClose={onClose}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Deactivate Item</Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{displayLabel}</strong> has a balance of <strong>{item.balance}</strong>.
          {item.balance !== 0 && ' Items with non-zero balance cannot be deactivated — re-link instead.'}
        </Alert>

        {item.balance === 0 && (
          <Typography variant="body2" color="text.secondary">
            This item has zero balance and {item.movementCount} historical movement
            {item.movementCount !== 1 ? 's' : ''}. The item will be soft-deleted.
            Movements are preserved.
          </Typography>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDeactivate}
          disabled={item.balance !== 0 || loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <DeactivateIcon />}
        >
          {loading ? 'Deactivating…' : 'Deactivate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Orphaned items tab ────────────────────────────────────────────────────────

const OrphanedItemsTab: React.FC = () => {
  const theme    = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading]         = useState(true);
  const [items, setItems]             = useState<OrphanedItem[]>([]);
  const [totalItems, setTotalItems]   = useState(0);
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [relinkItem, setRelinkItem]       = useState<OrphanedItem | null>(null);
  const [deactivateItem, setDeactivateItem] = useState<OrphanedItem | null>(null);
  const [snackbar, setSnackbar]           = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await repairService.getOrphanedItems(page + 1, rowsPerPage);
      setItems(data.data);
      setTotalItems(data.pagination.totalItems);
    } catch (err) {
      console.error('Failed to fetch orphaned items:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleDone = (msg: string) => {
    setRelinkItem(null);
    setDeactivateItem(null);
    setSnackbar(msg);
    fetchItems();
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          Active inventory items whose linked wine has been deactivated (e.g., after a merge).
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchItems} disabled={loading} size="small">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Paper elevation={0} sx={{
          p: 4, textAlign: 'center', borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          <Typography variant="body1" color="text.secondary">
            No orphaned inventory items found.
          </Typography>
        </Paper>
      ) : (
        <>
          <Paper elevation={0} sx={{
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
            overflow: 'hidden',
          }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.5) }}>
                  <TableCell sx={{ fontWeight: 700 }}>Item (inactive wine)</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 80 }}>Balance</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 90 }}>Movements</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 100 }}>Candidates</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 100 }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 120 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(item => {
                  const displayName = item.wineDisplay?.name || item.inactiveWine.name || '(unknown)';
                  const hasBalance  = item.balance !== 0;
                  const hasCands    = item.candidates.length > 0;
                  return (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{displayName}</Typography>
                        {item.wineDisplay?.producerName && (
                          <Typography variant="caption" color="text.secondary">
                            {item.wineDisplay.producerName}
                            {item.wineDisplay.displayVintage ? ` · ${item.wineDisplay.displayVintage}` : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={hasBalance ? 700 : 400}
                          color={hasBalance ? 'warning.main' : 'text.secondary'}
                        >
                          {item.balance}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{item.movementCount}</Typography>
                      </TableCell>
                      <TableCell>
                        {hasCands ? (
                          <Chip
                            label={`${item.candidates.length} found`}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        ) : (
                          <Chip
                            label="None"
                            size="small"
                            color="default"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="View movement ledger">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/admin/venues/${item.venueId}/inventory/${item.id}`)}
                            >
                              <OpenIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Re-link to active wine">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => setRelinkItem(item)}
                                disabled={!hasCands}
                                color="primary"
                              >
                                <RelinkIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={hasBalance ? 'Cannot deactivate — non-zero balance' : 'Deactivate'}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => setDeactivateItem(item)}
                                disabled={hasBalance}
                                color="error"
                              >
                                <DeactivateIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>

          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}

      {relinkItem && (
        <RelinkDialog
          item={relinkItem}
          onClose={() => setRelinkItem(null)}
          onDone={handleDone}
        />
      )}
      {deactivateItem && (
        <DeactivateDialog
          item={deactivateItem}
          onClose={() => setDeactivateItem(null)}
          onDone={handleDone}
        />
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

// ── Link WLI dialog ───────────────────────────────────────────────────────────

interface LinkWliDialogProps {
  wli:     UnlinkedWli;
  onClose: () => void;
  onDone:  (msg: string) => void;
}

const LinkWliDialog: React.FC<LinkWliDialogProps> = ({ wli, onClose, onDone }) => {
  const [selectedItemId, setSelectedItemId] = useState<string>(
    wli.candidates.length === 1 ? wli.candidates[0].inventoryItemId : ''
  );
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const handleLink = async () => {
    if (!selectedItemId) return;
    setError(null);
    setLoading(true);
    try {
      await repairService.linkWli(wli.id, selectedItemId);
      onDone('Wine list item successfully linked.');
    } catch (e: any) {
      setError(e?.response?.data?.error?.message ?? 'Link failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const displayLabel = wli.wineDisplay?.name || '(unnamed wine)';

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={onClose}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Link Inventory Item</Typography>
        <Typography variant="body2" color="text.secondary">{displayLabel}</Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          This wine list entry has no linked inventory item. Choose an active inventory item
          for this wine at this venue.
        </Alert>

        {wli.candidates.length === 0 ? (
          <Alert severity="warning">
            No active inventory items found for this wine at this venue.
            Create an inventory item first.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Select inventory item
            </Typography>
            <RadioGroup value={selectedItemId} onChange={e => setSelectedItemId(e.target.value)}>
              {wli.candidates.map((c: InventoryItemCandidate) => (
                <FormControlLabel
                  key={c.inventoryItemId}
                  value={c.inventoryItemId}
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {c.wineDisplay ? (c.wineDisplay as any).name || displayLabel : displayLabel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.bottleSize ? `${c.bottleSize}ml` : 'No bottle size'}
                        {c.locationId ? ` · Location set` : ' · No location'}
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 0.5, alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
                />
              ))}
            </RadioGroup>
          </>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          onClick={handleLink}
          disabled={!selectedItemId || wli.candidates.length === 0 || loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <LinkIcon />}
        >
          {loading ? 'Linking…' : 'Link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Unlinked WLIs tab ─────────────────────────────────────────────────────────

const UnlinkedWlisTab: React.FC = () => {
  const theme = useTheme();

  const [venues, setVenues]           = useState<Venue[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(true);
  const [selectedVenueId, setSelectedVenueId] = useState<string>('');

  const [loading, setLoading]         = useState(false);
  const [wlis, setWlis]               = useState<UnlinkedWli[]>([]);
  const [totalItems, setTotalItems]   = useState(0);
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [linkWli, setLinkWli]   = useState<UnlinkedWli | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // Load venues once
  useEffect(() => {
    venueService.getAllVenues(1, 100)
      .then(data => setVenues(data.data.filter(v => v.active)))
      .catch(err => console.error('Failed to load venues:', err))
      .finally(() => setVenuesLoading(false));
  }, []);

  const fetchWlis = useCallback(async () => {
    if (!selectedVenueId) return;
    setLoading(true);
    try {
      const data = await repairService.getUnlinkedWlis(selectedVenueId, page + 1, rowsPerPage);
      setWlis(data.data);
      setTotalItems(data.pagination.totalItems);
    } catch (err) {
      console.error('Failed to fetch unlinked WLIs:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedVenueId, page, rowsPerPage]);

  useEffect(() => {
    if (selectedVenueId) fetchWlis();
    else { setWlis([]); setTotalItems(0); }
  }, [fetchWlis, selectedVenueId]);

  const handleVenueChange = (venueId: string) => {
    setSelectedVenueId(venueId);
    setPage(0);
  };

  const handleDone = (msg: string) => {
    setLinkWli(null);
    setSnackbar(msg);
    fetchWlis();
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
          Wine list entries with no linked inventory item, or whose linked item has been deactivated.
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Select venue</InputLabel>
            <Select
              label="Select venue"
              value={selectedVenueId}
              onChange={e => handleVenueChange(e.target.value)}
              disabled={venuesLoading}
            >
              {venues.map(v => (
                <MenuItem key={v._id} value={v._id}>{v.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                onClick={fetchWlis}
                disabled={!selectedVenueId || loading}
                size="small"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {!selectedVenueId ? (
        <Paper elevation={0} sx={{
          p: 4, textAlign: 'center', borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          <Typography variant="body1" color="text.secondary">
            Select a venue to view unlinked wine list items.
          </Typography>
        </Paper>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : wlis.length === 0 ? (
        <Paper elevation={0} sx={{
          p: 4, textAlign: 'center', borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          <Typography variant="body1" color="text.secondary">
            No unlinked wine list items for this venue.
          </Typography>
        </Paper>
      ) : (
        <>
          <Paper elevation={0} sx={{
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
            overflow: 'hidden',
          }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.5) }}>
                  <TableCell sx={{ fontWeight: 700 }}>Wine</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 100 }}>Price</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 100 }}>Candidates</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 100 }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 80 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wlis.map(wli => {
                  const displayName = wli.wineDisplay?.name || '(unnamed wine)';
                  const hasCands    = wli.candidates.length > 0;
                  return (
                    <TableRow key={wli.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{displayName}</Typography>
                        {wli.wineDisplay?.producerName && (
                          <Typography variant="caption" color="text.secondary">
                            {wli.wineDisplay.producerName}
                            {wli.wineDisplay.displayVintage ? ` · ${wli.wineDisplay.displayVintage}` : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {wli.listPrice != null
                            ? `${wli.currency ?? ''} ${wli.listPrice.toFixed(2)}`
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {hasCands ? (
                          <Chip
                            label={`${wli.candidates.length} found`}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        ) : (
                          <Chip
                            label="None"
                            size="small"
                            color="default"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(wli.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={hasCands ? 'Link inventory item' : 'No candidates available'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setLinkWli(wli)}
                              disabled={!hasCands}
                              color="primary"
                            >
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>

          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}

      {linkWli && (
        <LinkWliDialog
          wli={linkWli}
          onClose={() => setLinkWli(null)}
          onDone={handleDone}
        />
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

type RepairTab = 0 | 1;

const Repair: React.FC = () => {
  const [activeTab, setActiveTab] = useState<RepairTab>(0);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>Re-link / Repair</Typography>
        <Typography variant="body2" color="text.secondary">
          Fix data integrity issues: orphaned inventory items and unlinked wine list entries.
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v as RepairTab)}
        sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Tab
          icon={<UnlinkIcon fontSize="small" />}
          iconPosition="start"
          label="Orphaned Items"
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }}
        />
        <Tab
          icon={<LinkIcon fontSize="small" />}
          iconPosition="start"
          label="Unlinked Wine List Items"
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }}
        />
      </Tabs>

      {activeTab === 0 && <OrphanedItemsTab />}
      {activeTab === 1 && <UnlinkedWlisTab />}
    </Box>
  );
};

export default Repair;
